/**
 * Host canonicalisation for deep links (BNF-375).
 *
 * The session token lives in `localStorage`, which is scoped per web origin.
 * A deep link on the `www.` sub-host (e.g. `www.mon-yeati.co/...`) therefore
 * starts with an empty `localStorage`: the route guard refresh fails with
 * "No access token available" and the user is bounced to `/connexion`, while
 * the same path on the apex host — where the logged-in session lives — opens
 * the content normally.
 *
 * Redirecting `www.` to the apex host, preserving the full path, lets an
 * already authenticated user land on the intended content instead of the
 * login page. Apex requests are left untouched.
 */

const WWW_PREFIX = "www.";

/**
 * Returns the apex URL to redirect to when the current location is on a `www.`
 * sub-host, preserving path, query string and hash. Returns `null` when the
 * host is already the apex (nothing to do).
 */
export const getApexRedirectUrl = (location: Location): string | null => {
  const {
    protocol, hostname, port, pathname, search, hash,
  } = location;

  if (!hostname.startsWith(WWW_PREFIX)) return null;

  const apexHost = hostname.slice(WWW_PREFIX.length);
  const portSegment = port ? `:${port}` : "";

  return `${protocol}//${apexHost}${portSegment}${pathname}${search}${hash}`;
};

/**
 * Redirects a `www.` deep link to the apex host before the app boots, so the
 * origin-scoped session token is available and the route guard resolves. Uses
 * `replace` to avoid leaving the `www.` URL in history. Returns `true` when a
 * redirect is under way.
 */
export const redirectWwwToApex = (): boolean => {
  const target = getApexRedirectUrl(window.location);
  if (!target) return false;
  window.location.replace(target);
  return true;
};
